import { Fragment, useEffect, useId, useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoTexto, type OpcaoDeSelecao } from '@/componentes/Campo';
import { CampoDaContingencia } from '@/componentes/CampoDaContingencia';
import { CampoDeCep, type EnderecoDoCep } from '@/componentes/CampoDeCep';
import { CamposDoEndereco } from '@/componentes/CamposDoEndereco';
import { Barra, Carregando, ControleCarregando } from '@/componentes/Esqueleto';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { BotaoDoRaro } from '@/componentes/LinhaDoPreenchido';
import { ehCampoDoEndereco, enderecoDe } from '@/componentes/resumo-do-endereco';
import { CADASTROS_COM_MESTRE, contingenciaValida, recusaDoMestre } from '@/lib/contingencia';
import { textoDoRascunho, useRascunho } from '@/lib/rascunho';
import { comVersao } from '@/lib/versao-da-ficha';
import { useSessao } from '@/modulos/sessao/consultas';

import { MensagemDeErro } from './Avisos';
import { sugestaoDaReferencia, valoresIniciais } from './autopreenchimento';
import { chaveDoRascunhoDoCadastro } from './rascunho-do-cadastro';
import { CAMPOS_DO_CADASTRO, corpoDe, lugarDoFormulario, type CampoDoFormulario, type Referencia } from './campos';
import { useReferencias } from './consultas';
import { documentoAoDigitar } from './documento';
import { rotuloDe, type LinhaDeCadastro, type TipoDeCadastro } from './tipos';

/**
 * O formulario dos seis cadastros: FORMULARIO EM GRADE (R01, p.28), dentro da
 * janela da pagina (R01, p.29) — o titulo e o "✕" sao da janela.
 *
 * Quatro regras do manual estao aqui, e nenhuma delas e estilo:
 *
 * 1. **Grade, com secoes e a largura pelo dado.** `formulario-grade` da 3, 2
 *    ou 1 coluna pelo espaco que tem. Os campos vem agrupados nas secoes da
 *    tabela de campos (Identificacao, Vinculos, Localizacao, Vigencia), cada
 *    uma com titulo. Codigo, UF, documento e data ocupam 1 coluna; nome e
 *    descricao, 2 (`campo-largo`); o motivo, a linha inteira. Os botoes NAO
 *    moram aqui: vao no rodape fixo da janela (R01, p.29: cabecalho e rodape
 *    fixos, so o corpo rola), e o de gravar alcanca este formulario pelo
 *    atributo `form` com o `id` dele. Dentro do corpo que rola, a barra
 *    deixava o conteudo aparecer por baixo dela e, em formulario curto, nao
 *    chegava ao pe da tela.
 * 2. **Valida AO SAIR do campo**, nunca a cada tecla — quem digita "12" a
 *    caminho de "12345678000195" nao errou nada, e o sistema que grita no
 *    segundo caractere ensina a pessoa a ignorar mensagem de erro. O erro
 *    aparece ABAIXO DO CAMPO (p.16), inclusive o de campo obrigatorio vazio. A
 *    conferencia do envio e so a rede: ela marca cada campo que falta e leva o
 *    foco ao primeiro.
 * 3. **Marca o OPCIONAL, nunca o obrigatorio.** Nao ha asterisco nesta tela, e
 *    nao ha como desenhar um: a marca vem da tabela de campos.
 * 4. **Uma unica acao em laranja.** Enquanto a janela esta aberta, o botao de
 *    gravar dela e o unico laranja que se alcanca.
 *
 * O que ele NAO faz: decidir. Duplicidade, referencia entre empresas e campo
 * obrigatorio sao do banco. O que chega de volta aparece inteiro, com o que
 * fazer, em `MensagemDeErro`.
 */
export function FormularioDeCadastro({
  id,
  tipo,
  linha,
  aoGravar,
  erro,
}: {
  /** O `id` do `<form>`: o botao de gravar, no rodape da janela, aponta para ele. */
  readonly id: string;
  readonly tipo: TipoDeCadastro;
  /** `null` para cadastrar; a linha para editar. */
  readonly linha: LinhaDeCadastro | null;
  readonly aoGravar: (campos: Record<string, unknown>) => void;
  readonly erro: unknown;
}) {
  const referencias = useReferencias();
  const base = useId();
  const sessao = useSessao();

  /* O que o cadastro novo ja vem sabendo (`autopreenchimento.ts`): o inicio
     da vigencia e hoje, com a marca de pre-preenchido. */
  const [inicial] = useState(() => valoresIniciais(tipo, linha, new Date()));
  const [valores, setValores] = useState<Record<string, string>>(() => ({ ...inicial.valores }));
  /* A versao de QUANDO O FORMULARIO ABRIU (robos de 20/10/2026, achado 1): a
     lista relida traria a nova, e o formulario velho gravaria por cima. */
  const [versao] = useState<string | null>(() => linha?.versao ?? null);

  /* O que o SISTEMA escreveu (o CEP, a data de hoje, a filial unica), ate a
     pessoa mexer: o campo diz "pré-preenchido" (decisao 12). */
  const [preenchidos, setPreenchidos] = useState<ReadonlySet<string>>(inicial.preenchidos);
  const [motivo, setMotivo] = useState('');
  const [escrevendoMotivo, setEscrevendoMotivo] = useState(false);
  const [erros, setErros] = useState<Readonly<Record<string, string>>>({});

  /* Trocar a UF limpa o municipio: "Ipatinga" com UF "SP" e um endereco que nao
     existe, e deixa-lo la guardaria exatamente o erro que esta tela impede. */
  function mudar(nome: string, novo: string) {
    setErros((atual) => Object.fromEntries(Object.entries(atual).filter(([chave]) => chave !== nome)));
    setPreenchidos((atual) => (atual.has(nome) ? new Set([...atual].filter((n) => n !== nome)) : atual));
    setValores((atual) => (nome === 'uf' ? { ...atual, uf: novo, municipio: '' } : { ...atual, [nome]: novo }));
  }

  /* O CEP preenche o RESTO do endereco de uma vez — e o preenchido e SUGESTAO:
     quem cadastra confere e grava, como a decisao 12 manda para tudo que o
     sistema le sozinho. */
  function preencherPeloCep(achado: EnderecoDoCep) {
    setValores((atual) => ({ ...atual, ...achado }));
    /* O CEP foi a pessoa que digitou; o resto, o provedor escreveu. */
    setPreenchidos((atual) => new Set([...atual, ...Object.keys(achado).filter((nome) => nome !== 'cep')]));
  }

  /* 0109: com o ERP mestre do cadastro, a recusa da regra I6 abre a
     contingência — o motivo vai na mesma gravação, para quem tem a licença. */
  const podeContingencia = (sessao.data?.permissoes ?? []).includes('integracao.contingencia');
  const recusa = CADASTROS_COM_MESTRE.has(tipo) ? recusaDoMestre(erro) : null;
  const [contingencia, setContingencia] = useState('');
  const [faltaContingencia, setFaltaContingencia] = useState(false);

  const opcoesDe = useMemo(
    () => ({
      filiais: comoOpcoes(referencias.filiais.data),
      clientes: comoOpcoes(referencias.clientes.data),
      'centros-de-custo': comoOpcoes(referencias.centros.data),
    }),
    [referencias.filiais.data, referencias.clientes.data, referencias.centros.data],
  );
  const carregandoDe = {
    filiais: referencias.filiais.isPending,
    clientes: referencias.clientes.isPending,
    'centros-de-custo': referencias.centros.isPending,
  };

  const campos = CAMPOS_DO_CADASTRO[tipo];
  const secoes = [...new Set(campos.map((campo) => campo.secao))];
  /* Os TITULOS DE SECAO so na pagina inteira (obras). Na janela, com tres a
     seis campos, eles empurravam o formulario para baixo e faziam rolar o
     que cabia de uma vez (pedido do dono, 31/10/2026: "sem rolar e mexer no
     mouse a toa"). */
  const comSecoes = lugarDoFormulario(tipo) === 'pagina';
  const idDo = (campo: CampoDoFormulario) => `${base}-${campo.nome}`;

  /* A FILIAL UNICA, uma vez, quando a lista chega (`autopreenchimento.ts`). */
  const [sugeriuFilial, setSugeriuFilial] = useState(false);
  const opcoesDaFilial = referencias.filiais.data === undefined ? undefined : opcoesDe.filiais.map((o) => o.valor);
  useEffect(() => {
    if (sugeriuFilial) return;
    const campo = campos.find((c) => c.referencia === 'filiais');
    if (campo === undefined || opcoesDaFilial === undefined) return;
    setSugeriuFilial(true);
    const sugerida = sugestaoDaReferencia({
      referencia: campo.referencia,
      cadastrando: linha === null,
      valor: valores[campo.nome] ?? '',
      opcoes: opcoesDaFilial,
    });
    if (sugerida === null) return;
    setValores((atual) => ({ ...atual, [campo.nome]: sugerida }));
    setPreenchidos((atual) => new Set([...atual, campo.nome]));
  }, [sugeriuFilial, campos, opcoesDaFilial, linha, valores]);

  /* O RASCUNHO (p.16: "conexão em obra cai, e o preenchimento não pode se
     perder"). Por empresa, pessoa, cadastro e registro, neste navegador. Leva
     só o que a PESSOA escreveu: o que o sistema preencheu volta sozinho, e
     sem isto o formulário recém-aberto, com a data de hoje e a filial única,
     já contaria como rascunho. Sai quando a gravação dá certo
     (`esquecerRascunhoDoCadastro`, na página). */
  const doPessoa = useMemo(
    () => ({
      valores: Object.fromEntries(Object.entries(valores).filter(([nome]) => !preenchidos.has(nome))),
      motivo,
    }),
    [valores, preenchidos, motivo],
  );
  const rascunho = useRascunho(
    chaveDoRascunhoDoCadastro(tipo, linha?.id ?? null, sessao.data ?? null),
    doPessoa,
    (guardado) => {
      setValores((atual) => ({ ...atual, ...guardado.valores }));
      setPreenchidos((atual) => new Set([...atual].filter((nome) => !(nome in guardado.valores))));
      setMotivo(guardado.motivo);
      if (guardado.motivo.trim() !== '') setEscrevendoMotivo(true);
    },
  );

  function descartarRascunho() {
    rascunho.descartar();
    setValores({ ...inicial.valores });
    setPreenchidos(inicial.preenchidos);
    setMotivo('');
    setErros({});
    setSugeriuFilial(false);
  }

  function marcar(campo: CampoDoFormulario, mensagem: string | null) {
    setErros((atual) => {
      const resto = Object.fromEntries(Object.entries(atual).filter(([chave]) => chave !== campo.nome));
      return mensagem ? { ...resto, [campo.nome]: mensagem } : resto;
    });
  }

  function enviar() {
    /* A rede do envio: a mesma regra de antes (obrigatorio vazio nao sai
       daqui), agora apontada no proprio campo, e com o foco no primeiro. */
    const falta = campos.filter((campo) => campo.especie !== 'sistema' && !campo.opcional && (valores[campo.nome] ?? '').trim() === '');
    if (falta.length > 0) {
      setErros((atual) => ({
        ...atual,
        ...Object.fromEntries(falta.map((campo) => [campo.nome, faltaPreencher(campo)])),
      }));
      const primeiro = falta[0];
      if (primeiro) document.getElementById(idDo(primeiro))?.focus();
      return;
    }
    if (recusa && podeContingencia) {
      if (!contingenciaValida(contingencia)) { setFaltaContingencia(true); return; }
      aoGravar(comVersao({ ...corpoDe(tipo, valores, motivo), contingencia: contingencia.trim() }, versao));
      return;
    }
    aoGravar(comVersao(corpoDe(tipo, valores, motivo), versao));
  }

  /** Um campo da tabela, no lugar dele da grade. O endereco entra de uma vez, no CEP. */
  function desenhar(campo: CampoDoFormulario) {
    if (ehCampoDoEndereco(campo.nome)) {
      if (campo.nome !== 'cep') return null;
      return (
        <Fragment key="endereco">
          <div className="min-w-0">
            <Campo
              id={idDo(campo)}
              rotulo={campo.rotulo}
              opcional={campo.opcional ?? false}
              erro={erros['cep'] ?? null}
              /* Depois de digitado, quem fala e o proprio CEP ("Endereço
                 preenchido pelo CEP..."): duas frases embaixo era uma demais. */
              {...(campo.ajuda && (valores['cep'] ?? '') === '' ? { ajuda: campo.ajuda } : {})}
            >
              <CampoDeCep
                id={idDo(campo)}
                valor={valores['cep'] ?? ''}
                aoMudar={(novo) => { mudar('cep', novo); }}
                aoSair={(atual) => { marcar(campo, conferir(campo, atual)); }}
                aoPreencher={preencherPeloCep}
                {...(erros['cep'] ? { 'aria-invalid': true, 'aria-describedby': `${idDo(campo)}-erro` } : {})}
              />
            </Campo>
          </div>
          <CamposDoEndereco
            base={base}
            valores={enderecoDe(valores)}
            preenchidos={preenchidos}
            erros={erros}
            aoMudar={mudar}
            aoSair={(nome, atual) => {
              const doEndereco = campos.find((c) => c.nome === nome);
              if (doEndereco) marcar(doEndereco, conferir(doEndereco, atual));
            }}
          />
        </Fragment>
      );
    }
    return (
      <div key={campo.nome} className={CAMPOS_LARGOS.has(campo.nome) ? 'campo-largo min-w-0' : 'min-w-0'}>
        <LinhaDoFormulario
          id={idDo(campo)}
          campo={campo}
          valor={valores[campo.nome] ?? ''}
          preenchido={preenchidos.has(campo.nome)}
          ajuda={ajudaDe(campo, valores, preenchidos)}
          erro={erros[campo.nome] ?? null}
          opcoes={campo.referencia ? opcoesDe[campo.referencia] : undefined}
          carregando={campo.referencia ? carregandoDe[campo.referencia] : false}
          aoMudar={(novo) => { mudar(campo.nome, novo); }}
          aoSair={(atual) => {
            marcar(campo, conferir(campo, atual));
          }}
        />
      </div>
    );
  }

  return (
    <form
      id={id}
      noValidate
      className="formulario-grade"
      onSubmit={(evento) => {
        evento.preventDefault();
        enviar();
      }}
    >
      {rascunho.recuperadoEm ? (
        <div
          role="status"
          className="campo-linha flex flex-wrap items-center justify-between gap-2 rounded-controle border border-borda bg-rebaixada px-4 py-2"
        >
          <span className="txt-apoio text-tinta">{textoDoRascunho(rascunho.recuperadoEm)}</span>
          <Botao nivel="secundario" tamanho="p" onClick={descartarRascunho}>
            Descartar rascunho
          </Botao>
        </div>
      ) : null}

      {secoes.map((secao) => (
        <Fragment key={secao}>
          {comSecoes ? <h3 className="txt-subtitulo campo-linha text-tinta">{secao}</h3> : null}
          {campos.filter((campo) => campo.secao === secao).map(desenhar)}
        </Fragment>
      ))}

      {/* O motivo da OPERACAO. Nao e coluna de nenhuma destas tabelas: vai para
          a trilha de auditoria, que e onde a pergunta "por que isso mudou?" se
          responde meses depois. E opcional e raro: fica atras do botao, como o
          "Mais dados" do lancamento de abastecimento (31/10/2026). */}
      {comSecoes ? <h3 className="txt-subtitulo campo-linha text-tinta">Histórico</h3> : null}
      <BotaoDoRaro
        aberto={escrevendoMotivo}
        aoAlternar={() => { setEscrevendoMotivo((atual) => !atual); }}
        rotulo="Escrever o motivo"
        rotuloAberto="Fechar o motivo"
        idDoConteudo={`${base}-motivo`}
      />
      {escrevendoMotivo ? (
        <div id={`${base}-motivo`} className="campo-linha">
          <CampoTexto
            rotulo="Motivo"
            valor={motivo}
            opcional
            ajuda="Fica registrado na trilha junto com o seu nome e a data."
            aoMudar={setMotivo}
          />
        </div>
      ) : null}

      {recusa ? (
        <div className="campo-linha flex flex-col gap-2">
          <CampoDaContingencia recusa={recusa} podeDeclarar={podeContingencia} motivo={contingencia} aoMudar={(v) => { setContingencia(v); setFaltaContingencia(false); }} />
          {faltaContingencia ? <p role="alert" className="txt-apoio text-parada-texto">Escreva o motivo da contingência com pelo menos 15 caracteres para gravar.</p> : null}
        </div>
      ) : erro ? (
        <div className="campo-linha">
          <MensagemDeErro erro={erro} />
        </div>
      ) : null}
    </form>
  );
}

/**
 * A ajuda do campo, quando o estado de agora pede outra frase que a da tabela:
 * de onde veio o valor pre-preenchido, e o cliente que o tipo da obra pede.
 */
function ajudaDe(campo: CampoDoFormulario, valores: Record<string, string>, preenchidos: ReadonlySet<string>): string | undefined {
  if (preenchidos.has(campo.nome)) {
    if (campo.nome === 'inicio') return 'Hoje. Troque se a vigência começou em outra data.';
    if (campo.referencia === 'filiais') return 'A única filial da empresa. Troque por "Sem filial" se não for dela.';
  }
  if (campo.nome === 'clienteId' && 'tipo' in valores) {
    return valores['tipo'] === 'obra'
      ? 'Obrigatório na obra de cliente.'
      : 'Pátio e oficina são da própria empresa: pode ficar sem cliente.';
  }
  return campo.ajuda;
}

/** Nome e descricao ocupam 2 colunas (R01); o resto, 1. */
const CAMPOS_LARGOS: ReadonlySet<string> = new Set(['nome', 'descricao']);

/** O que o leitor de tela ouve enquanto a lista de uma referencia chega. */
const CARREGANDO_A_REFERENCIA = {
  filiais: 'Carregando as filiais',
  clientes: 'Carregando os clientes',
  'centros-de-custo': 'Carregando os centros de custo',
} as const;

/**
 * O formulario enquanto o registro a editar ainda vem (p.32). As secoes, os
 * rotulos e as ajudas ja se sabem pela tabela de campos — so o controle vira
 * barra, na mesma grade, e o formulario de verdade chega sem a tela pular.
 */
export function EsqueletoDoFormulario({ tipo, oQue }: { readonly tipo: TipoDeCadastro; readonly oQue: string }) {
  const campos = CAMPOS_DO_CADASTRO[tipo];
  const secoes = [...new Set(campos.map((campo) => campo.secao))];
  const comSecoes = lugarDoFormulario(tipo) === 'pagina';
  /* O endereco chega como o formulario o desenha: CEP e numero a vista, e a
     linha da etiqueta no lugar dos outros quatro. */
  const visivel = (campo: CampoDoFormulario) => !ehCampoDoEndereco(campo.nome) || campo.nome === 'cep' || campo.nome === 'numero';
  return (
    <Carregando oQue={oQue}>
      <div aria-hidden="true" className="formulario-grade">
        {secoes.map((secao) => (
          <Fragment key={secao}>
            {comSecoes ? <h3 className="txt-subtitulo campo-linha text-tinta">{secao}</h3> : null}
            {campos
              .filter((campo) => campo.secao === secao && visivel(campo))
              .map((campo) => (
                <div key={campo.nome} className={CAMPOS_LARGOS.has(campo.nome) ? 'campo-largo min-w-0' : 'min-w-0'}>
                  <CampoCarregando rotulo={campo.rotulo} opcional={campo.opcional ?? false} ajuda={campo.ajuda} />
                </div>
              ))}
            {campos.some((campo) => campo.secao === secao && campo.nome === 'cep') ? (
              <div className="campo-linha flex items-center gap-3">
                <span className="txt-rotulo-campo">Endereço</span>
                <Barra className="h-6 w-60 rounded-etiqueta" />
              </div>
            ) : null}
          </Fragment>
        ))}
        {comSecoes ? <h3 className="txt-subtitulo campo-linha text-tinta">Histórico</h3> : null}
        <div className="campo-linha">
          <Barra className="h-8 w-40 rounded-controle" />
        </div>
      </div>
    </Carregando>
  );
}

/** O `Campo` sem o controle: o rotulo e a ajuda de verdade, e a barra de 40 px. */
function CampoCarregando({
  rotulo,
  opcional,
  ajuda,
}: {
  readonly rotulo: string;
  readonly opcional: boolean;
  readonly ajuda: string | undefined;
}) {
  return (
    <span className="flex min-w-0 flex-col gap-2">
      <span className="flex flex-wrap items-baseline gap-x-1">
        <span className="txt-rotulo-campo">{rotulo}</span>
        {opcional ? <span className="txt-ajuda">· opcional</span> : null}
      </span>
      <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
      {ajuda ? <span className="txt-ajuda">{ajuda}</span> : null}
    </span>
  );
}

/** O que falta, dito no campo e com o que fazer (p.16). */
function faltaPreencher(campo: CampoDoFormulario): string {
  return `Preencha “${campo.rotulo}” para poder gravar.`;
}

/** Ao sair do campo: o obrigatorio vazio e, depois, a regra propria do campo. */
function conferir(campo: CampoDoFormulario, valor: string): string | null {
  if (campo.especie === 'sistema') return null;
  if (!campo.opcional && valor.trim() === '') return faltaPreencher(campo);
  return campo.validar ? campo.validar(valor) : null;
}

/**
 * Um campo da tabela de campos, desenhado com o `Campo` comum para o erro
 * poder vir DE FORA: o do envio e o de sair do campo moram no formulario, e
 * `CampoTexto` so conhece o proprio.
 *
 * A ESCOLHA e o seletor do sistema (`ListaDeEscolha`, p.23 e p.31), e nao o
 * `<select>` nativo: com mais de sete opcoes ele abre com busca, e o codigo do
 * cadastro vem em etiqueta ao lado do nome. O obrigatorio com uma opcao so
 * vem escolhido e travado (`lib/escolha-unica.ts`).
 *
 * `<input type="date">` nativo, e nao um calendario proprio: ele ja fala o
 * formato do sistema operacional da pessoa, ja abre por teclado e ja recebe o
 * anel de foco da regra 8 pelo seletor global de `input`.
 */
function LinhaDoFormulario({
  id,
  campo,
  valor,
  preenchido,
  ajuda,
  erro,
  opcoes,
  carregando,
  aoMudar,
  aoSair,
}: {
  readonly id: string;
  readonly campo: CampoDoFormulario;
  readonly valor: string;
  /** Escrito pelo sistema, e ainda nao conferido pela pessoa (decisao 12). */
  readonly preenchido: boolean;
  readonly ajuda: string | undefined;
  readonly erro: string | null;
  readonly opcoes: readonly OpcaoDeSelecao[] | undefined;
  /** A lista da referencia ainda vem: o controle vira esqueleto (p.32). */
  readonly carregando: boolean;
  readonly aoMudar: (valor: string) => void;
  readonly aoSair: (valor: string) => void;
}) {
  const marcaDeErro = erro ? { 'aria-invalid': true, 'aria-describedby': `${id}-erro` } : {};

  /* 0131 · O CODIGO DO SISTEMA: so para ler, e nao e controle. Cadastrando,
     ele ainda nao existe, e a linha diz quando ele nasce. */
  if (campo.especie === 'sistema') {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <span className="txt-rotulo-campo">{campo.rotulo}</span>
        {valor === '' ? (
          <span className="txt-apoio text-tinta-secundaria">O código é criado pelo sistema ao salvar.</span>
        ) : (
          <span className="codigo text-tinta">{valor}</span>
        )}
      </div>
    );
  }

  /* O CPF ou CNPJ com a mascara enquanto se digita (`documento.ts`); o corpo
     continua indo so com os digitos. */
  const ehDocumento = campo.nome === 'documento';

  return (
    <Campo
      id={id}
      rotulo={campo.rotulo}
      opcional={campo.opcional ?? false}
      preenchido={preenchido}
      erro={erro}
      {...(ajuda ? { ajuda } : {})}
    >
      {carregando && campo.referencia ? (
        /* Sem as opcoes, a lista so com o vazio deixava escolher o nada — e,
           na edicao, escondia o valor que ja estava gravado. */
        <ControleCarregando oQue={CARREGANDO_A_REFERENCIA[campo.referencia]} />
      ) : campo.especie === 'selecao' ? (
        <ListaDeEscolha
          id={id}
          rotulo={campo.rotulo}
          valor={valor}
          aoMudar={aoMudar}
          aoSair={() => {
            aoSair(valor);
          }}
          rotuloVazio={vazioDe(campo)}
          opcoes={campo.opcoes ?? opcoes ?? []}
          idDoErro={erro ? `${id}-erro` : undefined}
        />
      ) : (
        <input
          id={id}
          type={campo.especie === 'data' ? 'date' : 'text'}
          autoComplete="off"
          value={ehDocumento ? documentoAoDigitar(valor) : valor}
          {...(ehDocumento ? { inputMode: 'numeric' as const, placeholder: '00.000.000/0000-00' } : {})}
          className="controle w-full"
          onChange={(evento) => {
            aoMudar(ehDocumento ? documentoAoDigitar(evento.target.value) : evento.target.value);
          }}
          onBlur={(evento) => {
            aoSair(evento.target.value);
          }}
          {...marcaDeErro}
        />
      )}
    </Campo>
  );
}

/**
 * A opcao vazia da lista, que diz o que ela e: "Sem filial" e resposta (o
 * campo opcional), "Escolha o cliente" e convite (o obrigatorio). A lista fixa
 * obrigatoria (o tipo da obra) nao tem vazio.
 */
function vazioDe(campo: CampoDoFormulario): string | undefined {
  const nomes: Record<Referencia, { readonly sem: string; readonly escolha: string }> = {
    filiais: { sem: 'Sem filial', escolha: 'Escolha a filial' },
    clientes: { sem: 'Sem cliente', escolha: 'Escolha o cliente' },
    'centros-de-custo': { sem: 'Sem centro de custo', escolha: 'Escolha o centro de custo' },
  };
  if (campo.referencia) return campo.opcional ? nomes[campo.referencia].sem : nomes[campo.referencia].escolha;
  return campo.opcional ? 'Sem escolha' : undefined;
}

/** O nome no rotulo e o codigo em etiqueta neutra (p.25), que entra na busca. */
function comoOpcoes(linhas: readonly LinhaDeCadastro[] | undefined): readonly OpcaoDeSelecao[] {
  return (linhas ?? [])
    .filter((linha) => linha.ativo)
    .map((linha) => ({
      valor: linha.id,
      rotulo: rotuloDe(linha),
      /* O codigo igual ao nome ("MATRIZ") seria a mesma palavra duas vezes. */
      ...(linha.codigo === rotuloDe(linha) ? {} : { etiquetas: [{ texto: linha.codigo, tom: 'neutro' as const, titulo: 'Código' }] }),
    }));
}
